import { X } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** One stop on a guided walk: a part of the screen and what to say about it. Used by the training
 * tour and by "Show me around this screen" in Help. */
export type GuideStep = {
  id: string;
  title: string;
  body?: React.ReactNode;
  /** The one thing to do now, shown in bold under the body ("Click Approve"). */
  doThis?: React.ReactNode;
  /** CSS selector(s); the first one visible on screen is spotlit. None: the card sits centred. */
  target?: string | string[];
  /** The screen this step lives on, for "Take me back" when the trainee has wandered off. */
  route?: string;
  role?: "Reviewer/Admin";
};

const reduceMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** The first matching element that is actually on screen (the side nav is in the DOM on a phone,
 * but has no size). */
export function findVisible(target: GuideStep["target"]): Element | null {
  if (!target) return null;
  for (const selector of [target].flat()) {
    for (const el of document.querySelectorAll(selector)) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return el;
    }
  }
  return null;
}

type Box = { top: number; left: number; width: number; height: number };

/** Follows the target through scrolling, resizing, lazy loading and layout shifts. One query per
 * frame is cheaper than wiring observers to elements that come and go. */
function useTargetBox(target: GuideStep["target"], stepId: string) {
  const [box, setBox] = useState<Box | null>(null);
  const [lost, setLost] = useState(false);
  useEffect(() => {
    if (!target) return;
    let frame = 0;
    let last = "";
    let scrolled = false;
    let lostNow = false;
    const started = performance.now();
    const tick = () => {
      const el = findVisible(target);
      if (el) {
        if (!scrolled) {
          scrolled = true;
          // On a phone the card is a sheet over half the screen: bring the part to the middle.
          const phone = window.innerWidth < 640;
          el.scrollIntoView({
            block: phone ? "center" : "nearest",
            behavior: reduceMotion() ? "auto" : "smooth",
          });
        }
        const r = el.getBoundingClientRect();
        const key = `${Math.round(r.top)},${Math.round(r.left)},${Math.round(r.width)},${Math.round(r.height)}`;
        if (key !== last) {
          last = key;
          setBox({ top: r.top, left: r.left, width: r.width, height: r.height });
        }
        if (lostNow) setLost((lostNow = false));
      } else {
        if (last !== "none") {
          last = "none";
          setBox(null);
        }
        // Screens load lazily; give them a moment before saying the part is not here.
        if (!lostNow && performance.now() - started > 2500) setLost((lostNow = true));
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      setBox(null);
      setLost(false);
    };
  }, [target, stepId]);
  return { box, lost };
}

const PAD = 6;
const GAP = 14;
const EDGE = 12;

/** Beside the target without covering it: below, above, right, left, then a corner. */
function placeCard(box: Box, w: number, h: number) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const clampX = (x: number) => Math.min(Math.max(EDGE, x), vw - w - EDGE);
  const clampY = (y: number) => Math.min(Math.max(EDGE, y), vh - h - EDGE);
  const centreX = box.left + box.width / 2 - w / 2;
  if (box.top + box.height + PAD + GAP + h <= vh - EDGE)
    return { top: box.top + box.height + PAD + GAP, left: clampX(centreX) };
  if (box.top - PAD - GAP - h >= EDGE)
    return { top: box.top - PAD - GAP - h, left: clampX(centreX) };
  if (box.left + box.width + PAD + GAP + w <= vw - EDGE)
    return { top: clampY(box.top), left: box.left + box.width + PAD + GAP };
  if (box.left - PAD - GAP - w >= EDGE)
    return { top: clampY(box.top), left: box.left - PAD - GAP - w };
  return { top: vh - h - EDGE, left: vw - w - EDGE };
}

function useNarrow() {
  const [narrow, setNarrow] = useState(() => window.matchMedia("(max-width: 639px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
}

export type CoachOverlayProps = {
  step: GuideStep;
  /** "Chapter 3 of 11 · Step 2 of 5": where the trainee is. */
  progress: string;
  /** Next / Continue / Finish. Absent when the step moves on by itself once the trainee acts. */
  primary?: { label: string; onClick: () => void };
  onBack?: () => void;
  onExit: () => void;
  exitLabel?: string;
  /** Extra content under the body: a stage map, a "skip ahead" control. */
  extra?: React.ReactNode;
  /** Shown when the step's part cannot be found on screen. */
  onReturn?: () => void;
  /** Dim the page even when the step has no target (chapter cards). */
  dim?: boolean;
  /** Called once if the step's part never shows up; the step is skipped instead of stuck. */
  onMissing?: () => void;
};

/** The spotlight and its coach card. Neither blocks the page: the trainee can click anything,
 * which is the point of a hands-on tour. */
export function CoachOverlay({
  step,
  progress,
  primary,
  onBack,
  onExit,
  exitLabel = "Exit tour",
  extra,
  onReturn,
  dim,
  onMissing,
}: CoachOverlayProps) {
  const { box, lost } = useTargetBox(step.target, step.id);
  const narrow = useNarrow();
  const cardRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const [size, setSize] = useState({ w: 360, h: 220 });
  const titleId = useId();

  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.offsetWidth, h: el.offsetHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // A step you read takes focus on its button; a step you act on leaves focus where you are.
  useEffect(() => {
    if (primary) primaryRef.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per step
  }, [step.id]);

  // Esc leaves, unless a dialog of the app is open: then Esc belongs to that dialog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (
        document.querySelector(
          '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
        )
      )
        return;
      onExit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onExit]);

  // An app drawer opening marks everything outside it aria-hidden (Radix, for modals). The coach
  // card is the one thing outside it that must stay readable, or a screen reader loses the tour.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    // It hides the overlay, or the card's rows around the live region it keeps. Only those
    // containers are un-hidden; our own decorative icons and bullets sit deeper and stay hidden.
    const keep = () => {
      const card = root.querySelector("[data-tour-card]");
      for (const el of [root, ...root.children, ...(card?.children ?? [])]) {
        if (!el.hasAttribute("aria-hidden") || el.hasAttribute("data-keep-hidden")) continue;
        el.removeAttribute("aria-hidden");
        el.removeAttribute("data-aria-hidden");
      }
    };
    const mo = new MutationObserver(keep);
    mo.observe(root, { attributes: true, attributeFilter: ["aria-hidden"], subtree: true });
    return () => mo.disconnect();
  }, []);

  useEffect(() => {
    if (lost && onMissing) onMissing();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once when the part goes missing
  }, [lost]);

  const waiting = !!step.target && !box && !lost;
  const missing = !!step.target && !box && lost;
  const spot = box && {
    top: box.top - PAD,
    left: box.left - PAD,
    width: box.width + PAD * 2,
    height: box.height + PAD * 2,
  };
  const pos = !narrow && box ? placeCard(box, size.w, size.h) : null;
  // A phone sheet sits at the bottom unless the part is in the lower half; then it goes on top.
  const sheetOnTop = narrow && !!box && box.top + box.height / 2 > window.innerHeight / 2;

  return createPortal(
    // Above toasts too (sonner sits at 999999999): a toast must never cover the card's button.
    <div
      ref={rootRef}
      data-training-overlay
      className="pointer-events-none fixed inset-0 z-[1000000000]"
    >
      {spot ? (
        <div
          aria-hidden
          data-keep-hidden
          className="coach-spot absolute rounded-[10px] motion-safe:transition-[top,left,width,height] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={spot}
        />
      ) : (
        (dim || missing) && (
          <div aria-hidden data-keep-hidden className="coach-dim absolute inset-0" />
        )
      )}

      <div
        ref={cardRef}
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        data-tour-card
        // A click in here must not count as "outside" to an open app dialog, or it would close.
        onPointerDown={(e) => e.stopPropagation()}
        className={cn(
          "bg-popover text-popover-foreground coach-card pointer-events-auto absolute flex flex-col gap-3 border p-4",
          narrow &&
            !sheetOnTop &&
            "inset-x-0 bottom-0 rounded-t-xl border-x-0 border-b-0 pb-[max(1rem,env(safe-area-inset-bottom))]",
          narrow && sheetOnTop && "inset-x-0 top-0 rounded-b-xl border-x-0 border-t-0",
          !narrow && "w-[min(368px,calc(100vw-24px))] rounded-lg",
          !narrow && !pos && "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
          !narrow &&
            pos &&
            "motion-safe:transition-[top,left] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.16,1,0.3,1)]",
          waiting && "opacity-0",
        )}
        style={pos ?? undefined}
      >
        <div className="flex items-start gap-3">
          <h2 id={titleId} className="min-w-0 flex-1 text-base leading-snug text-balance">
            {step.title}
          </h2>
          {step.role && (
            <span className="border-warn-bd bg-warn-bg text-warn shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap">
              {step.role} step
            </span>
          )}
          <button
            type="button"
            onClick={onExit}
            aria-label={exitLabel}
            className="text-muted-foreground hover:text-foreground hover:bg-muted -mt-1 -mr-1 grid size-7 shrink-0 place-items-center rounded-md"
          >
            <X aria-hidden className="size-4" />
          </button>
        </div>

        {missing ? (
          <div className="text-sm">
            <p>That part isn&rsquo;t on screen right now.</p>
            {onReturn && (
              <Button variant="outline" size="sm" className="mt-2" onClick={onReturn}>
                Take me back
              </Button>
            )}
          </div>
        ) : (
          <>
            {step.body && (
              <div className="text-foreground/90 space-y-2 text-sm leading-relaxed">
                {step.body}
              </div>
            )}
            {step.doThis && (
              <p className="text-foreground flex gap-2 text-sm font-semibold">
                <span aria-hidden className="bg-brand mt-[7px] size-1.5 shrink-0 rounded-full" />
                <span>{step.doThis}</span>
              </p>
            )}
          </>
        )}
        {extra}

        <div className="flex items-center gap-2 pt-1">
          <span className="text-muted-foreground mr-auto text-xs tabular-nums">{progress}</span>
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              Back
            </Button>
          )}
          {primary && (
            <Button ref={primaryRef} size="sm" onClick={primary.onClick}>
              {primary.label}
            </Button>
          )}
        </div>
        <p className="sr-only" aria-live="polite">
          {step.title}
        </p>
      </div>
    </div>,
    document.body,
  );
}
